import React from 'react';
import { createFocusAreaColumns } from '../muidatagrid/columnDefs/focusAreaColumns';
import MuiDataGrid from '../muidatagrid';
import AutomationFooter, { AutomationLink } from '../automation-footer';
import { FocusAreaData } from '../../models/operationsmodels/FocusAreaModels';
import { useAppSelector } from '../../redux/hooks';
import {
  selectKpisByKpiCodes,
  selectKpisByGridName,
} from '../../redux/OperationsReducer/operationsSlice';
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { useDrawerClickHandler } from '../../utils/drawerClickContext';

/**
 * Configuration for automation links
 */
export interface AutomationLinkConfig {
  label: string;
  urlCode: string;
  urlField?: string;
}

interface KpiFocusAreaProps {
  /** KPI codes to fetch data for */
  kpiCodes: string[];
  /** Optional grid name for additional KPI data (uses selectKpisByGridName) */
  gridName?: string;
  /** Card name for filtering KPI data (e.g., 'Inventory', 'Marketing', 'People', 'B2B', 'Ecommerce') - required when gridName is provided */
  cardName?: string;
  /** Configuration for automation links */
  automationLinks: AutomationLinkConfig[];
  /** Optional custom title for automation footer */
  automationTitle?: string;
  /** Base URL type for building dynamic URLs */
  baseUrlType?: 'WFC' | 'ACENET';
}

/**
 * KpiFocusArea - Reusable component for KPI Focus Area grids with automation links
 * Displays a focus area data grid and automation footer for KPI Take Action drawers
 *
 * @example
 * <KpiFocusArea
 *   kpiCodes={['MGIS']}
 *   automationLinks={[{ label: 'Mango In-Stock Opportunity', urlCode: 'MGIS', urlField: 'automationurl' }]}
 *   automationTitle="For Optimal Shelf Quantity Automation :"
 * />
 */
const KpiFocusArea: React.FC<KpiFocusAreaProps> = ({
  kpiCodes,
  gridName,
  cardName,
  automationLinks,
  automationTitle,
  baseUrlType = 'ACENET',
}) => {
  const kpisByCode = useAppSelector(state => selectKpisByKpiCodes(state, kpiCodes));
  const kpisByGrid = useAppSelector(state =>
    gridName && cardName ? selectKpisByGridName(state, gridName, cardName) : [],
  );

  // Combine and deduplicate KPI data
  const kpiData = [
    ...kpisByCode,
    ...kpisByGrid.filter(k => !kpisByCode.some(c => c.kpiCode === k.kpiCode)),
  ];
  
  const buildDynamicUrl = useBuildDynamicUrl();
  const clickHandler = useDrawerClickHandler();

  // Transform KPI data to focus area format
  const focusAreaData: FocusAreaData[] = kpiData.map(kpi => ({
    id: kpi.kpiCode,
    focusArea: kpi.description,
    kpiGoal: kpi.kpiGoal,
    acceptance: kpi.currentKpiAmount,
    takeActionTitle: `Review ${kpi.description}`,
    actionHyperlink: buildDynamicUrl(baseUrlType, kpi.kpiCode) ?? null,
    kpiFormat: kpi.kpiFormat,
    symbol: kpi.symbol,
    isFocusArea: true,
  }));

  // Build automation links with dynamic URLs
  const automationLinkData: AutomationLink[] = automationLinks.map(link => ({
    label: link.label,
    url: buildDynamicUrl(baseUrlType, link.urlCode, link.urlField),
  }));

  return (
    <>
      <MuiDataGrid
        rows={focusAreaData}
        getRowId={row => row.id}
        createColumns={createFocusAreaColumns}
        logClick={clickHandler}
      />
      <AutomationFooter
        title={automationTitle}
        links={automationLinkData}
        onLinkClick={
          clickHandler
            ? (label, url) =>
                clickHandler(`Take Action Drawer - ${label}`, { url })
            : undefined
        }
      />
    </>
  );
};

export default KpiFocusArea;
